// ============================================================================
// 바인더 시뮬 — 240Hz 고정. three.js 를 모른다. 렌더는 BinderRS 만 읽는다.
//
//   stow(수납) → idle → grab → move(flip / pan / edge) → settle → idle
//                idle → detail(상세 보기)
//
// 핵심 규칙 두 가지 (BINDER_DESIGN §6.3.4):
//   1. flip 과 pan 을 **같은 1자유도 좌표 u ∈ [0,1]** 로 다룬다.
//   2. **카메라는 u 의 함수다.** 따로 적분하지 않으므로 시트와 어긋날 수 없다.
//      그래서 RS 에도 viewX / theta 를 넣지 않고 u 만 넣는다 (렌더가 보간된 u 로 계산).
//
// 화면 비에 따라 두 모드다 (binder-layout.ts '펼침' 절):
//   포트레이트 — 한 면씩. 카메라가 그 면을 중앙에 둔다. flip/pan 이 홀짝으로 교대.
//   펼침(와이드) — 좌우 두 면 동시. 카메라는 등에 고정(viewX=0). 넘기기는 항상 flip.
// 모드는 aspect 하나로 갈린다. 상태(face)는 공용이고, 물리도 같은 stepFlip 을 쓴다.
// ============================================================================
import { P, WORLD, BINDER_LAYERS, CARD_COUNT } from './params';
import { PointerInput, Vec2 } from './input';
import { audio } from './audio';
import { haptics } from './haptics';
import { CARD_ASPECT, CARD_BY_ID, CELLS_PER_FACE, cardBySlot, faceCount, faceIsBack, faceToSlot, maxAssignedSlot, slotToCell, slotToFace } from './cards.ts';
import { BinderSaveV1, emptySave, saveBinder } from './save.ts';
import {
  CellBox,
  SEG_EDGE,
  SEG_FLIP,
  SEG_NONE,
  SEG_PAN,
  SegKindId,
  cardSizeInCell,
  cellBox,
  faceCenterX,
  faceHeight,
  faceWidth,
  isSpread,
  perspScale,
  screenWidth,
  spreadCanonFace,
  spreadLeftFace,
  spreadOf,
  spreadRightFace,
  thetaOf,
  viewXOf,
} from './binder-layout';

export type BinderMode = 'stow' | 'idle' | 'grab' | 'move' | 'settle' | 'detail';
export type DetailPhase = 'opening' | 'shown' | 'drag' | 'nav' | 'closing';
/**
 * 수납의 단계. 카드 **한 장씩** 이 흐름을 한 바퀴 돈다 (§2.3, 2026-09-16 개정).
 *   rise  바인더가 올라오는 동안 (stowDelay)
 *   turn  그 카드의 면이 안 보이면 거기까지 넘긴다 — 손으로 넘길 때와 같은 세그먼트를 자동으로 민다
 *   pause 면이 도착한 뒤의 뜸 (stowTurnPause). 넘기지 않았으면 건너뛴다
 *   drop  카드가 화면 밖에서 제 칸으로 내려앉는다
 *   gap   착지 뒤 다음 카드까지의 간격 (stowGap)
 */
export type StowPhase = 'rise' | 'turn' | 'pause' | 'drop' | 'gap';

const MODE_ID: Record<BinderMode, number> = { stow: 0, idle: 1, grab: 2, move: 3, settle: 4, detail: 5 };
const PHASE_ID: Record<DetailPhase, number> = { opening: 0, shown: 1, drag: 2, nav: 3, closing: 4 };

// ------------------------------------------------------------------ 렌더 상태
export class BinderRS {
  u = 0;
  riseY = 0;
  face = 1;
  mode = 1;
  segKind: number = SEG_NONE;
  segDir = 1;
  segFrom = 1;
  segTo = 1;
  segSheet = 1;
  // 수납 (한 팩 = CARD_COUNT 장 고정)
  sx = new Float64Array(CARD_COUNT);
  sy = new Float64Array(CARD_COUNT);
  ss = new Float64Array(CARD_COUNT); // 카드 폭 (unit)
  svis = new Float64Array(CARD_COUNT);
  sslot = new Float64Array(CARD_COUNT);
  slanded = new Float64Array(CARD_COUNT);
  // 상세 보기
  detActive = 0;
  detPhase = 0;
  detCell = 0;
  detSlotA = 0;
  detSlotB = 0;
  detOwnedA = 0;
  detOwnedB = 0;
  detBVis = 0;
  ax = 0;
  ay = 0;
  as = 0;
  az = 0;
  bx = 0;
  by = 0;
  bs = 0;
  bz = 0;
  dim = 0;

  static SCALARS = ['u', 'riseY', 'ax', 'ay', 'as', 'az', 'bx', 'by', 'bs', 'bz', 'dim'] as const;
  static DISCRETE = ['face', 'mode', 'segKind', 'segDir', 'segFrom', 'segTo', 'segSheet', 'detActive', 'detPhase', 'detCell', 'detSlotA', 'detSlotB', 'detOwnedA', 'detOwnedB', 'detBVis'] as const;
  static ARRAYS = ['sx', 'sy', 'ss'] as const;

  copyFrom(o: BinderRS): void {
    for (const k of BinderRS.SCALARS) this[k] = o[k];
    for (const k of BinderRS.DISCRETE) this[k] = o[k];
    for (const k of BinderRS.ARRAYS) this[k].set(o[k]);
    this.svis.set(o.svis);
    this.sslot.set(o.sslot);
    this.slanded.set(o.slanded);
  }

  /** this = lerp(a, b, t). 세그먼트·모드가 바뀐 프레임은 b 를 그대로 쓴다 (튀는 것을 막는다). */
  lerp(a: BinderRS, b: BinderRS, t: number): void {
    const snap =
      a.mode !== b.mode || a.segKind !== b.segKind || a.segFrom !== b.segFrom || a.segTo !== b.segTo || a.detPhase !== b.detPhase || a.detSlotA !== b.detSlotA;
    for (const k of BinderRS.SCALARS) this[k] = snap ? b[k] : a[k] + (b[k] - a[k]) * t;
    for (const k of BinderRS.DISCRETE) this[k] = b[k];
    for (const k of BinderRS.ARRAYS) {
      const A = a[k], B = b[k], O = this[k];
      for (let i = 0; i < CARD_COUNT; i++) O[i] = snap || a.svis[i] !== b.svis[i] ? B[i] : A[i] + (B[i] - A[i]) * t;
    }
    this.svis.set(b.svis);
    this.sslot.set(b.sslot);
    this.slanded.set(b.slanded);
  }
}

/** 상세 보기 상태. 카드 A(현재) / 카드 B(이웃으로 넘길 때 들어오는 카드). */
interface DetailState {
  phase: DetailPhase;
  stage: 1 | 2;
  slot: number;
  cell: number;
  owned: boolean;
  ax: number; ay: number; as: number; az: number;
  avx: number; avy: number; avs: number; avz: number;
  bActive: boolean; bSlot: number; bOwned: boolean;
  bx: number; by: number; bs: number; bz: number;
  bvx: number; bvy: number;
  dim: number; dimV: number;
  dragging: boolean;
  fx0: number; fy0: number; dragX: number; dragY: number;
  navDir: number;
  /** 드래그 축 잠금: 0 = 미정, 1 = 가로(넘기기), 2 = 세로(닫기). 제스처마다 한 번만 정한다. */
  axisLock: 0 | 1 | 2;
  homeX: number; homeY: number; homeS: number; homeZ: number;
}

interface PendingDown {
  p: Vec2;
  t: number;
}
interface PendingUp {
  p: Vec2;
  t: number;
  v: Vec2;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// ------------------------------------------------------------------ 시뮬
export class BinderSim {
  mode: BinderMode = 'idle';
  face = 1;
  time = 0;
  exitRequested = false;
  save: BinderSaveV1 = emptySave();
  rs = new BinderRS();
  prev = new BinderRS();
  onEvent: ((name: string) => void) | null = null;
  /** 손가락이 보유 카드 칸에 닿는 순간 원본 텍스처를 미리 요청한다 (탭 확정까지 tapMaxMs 를 번다). */
  onPrefetch: ((slot: number) => void) | null = null;

  seg = {
    kind: SEG_NONE as SegKindId,
    dir: 1,
    from: 1,
    to: 1,
    sheet: 1,
    u: 0,
    v: 0,
    peeled: false,
    uGrab0: 0,
    fx0: 0,
    tau: 0,
    edgeHit: false,
    lastSlap: -1,
    // 수납이 스스로 면을 넘길 때. **새 물리가 아니다** — 손가락 대신 가상 목표 u 를 쓰는 것뿐이고
    // targetU() 가 이 값을 돌려주면 flip/pan 이 평소와 똑같이 돈다.
    auto: false,
    autoU: 0,
  };

  grab = { x0: 0, y0: 0, t0: 0, cell: -1 };

  /** cellAt() 이 마지막으로 맞춘 면. 0 = 못 맞췄다. 펼침에서 좌우를 가른다. */
  hitFace = 0;
  private hitCand = new Int32Array(2);

  /**
   * 수납 상태. **한 팩을 슬롯 순서로 정렬해 한 장씩** 처리한다 (2026-09-16 사용자 요청).
   * 배열 index 는 뽑은 순서가 아니라 **정렬된 순서**다 — 0..n-1 이 슬롯 오름차순이고,
   * 그래서 면도 오름차순이라 바인더가 앞뒤로 왔다 갔다 하지 않는다.
   *
   * 예전에는 8장을 동시에 띄우고 현재 면이 아닌 카드는 등(spine)으로 빨아들여 없앴다 (`toSpine`).
   * 사용자가 그 연출을 빼라고 해서 **전부 제 페이지로 넘어가 들어가도록** 바꿨고 toSpine 은 지웠다.
   */
  stow = {
    active: false,
    t: 0, // 수납 전체 경과 (진단·도구용)
    ct: 0, // 지금 단계의 경과. 안전장치(stowDuration)는 **단계마다** 이 값을 본다
    phase: 'rise' as StowPhase,
    cur: 0, // 지금 처리 중인 카드
    turned: false, // 이 카드를 위해 면을 실제로 넘겼나 (넘겼을 때만 stowTurnPause 를 쓴다)
    riseY: 0,
    riseV: 0,
    slot: new Int32Array(CARD_COUNT),
    face: new Int32Array(CARD_COUNT),
    x: new Float64Array(CARD_COUNT),
    y: new Float64Array(CARD_COUNT),
    s: new Float64Array(CARD_COUNT),
    vx: new Float64Array(CARD_COUNT),
    vy: new Float64Array(CARD_COUNT),
    vs: new Float64Array(CARD_COUNT),
    tx: new Float64Array(CARD_COUNT),
    ty: new Float64Array(CARD_COUNT),
    ts: new Float64Array(CARD_COUNT),
    started: new Uint8Array(CARD_COUNT),
    landed: new Uint8Array(CARD_COUNT),
    live: new Uint8Array(CARD_COUNT),
    n: 0,
  };

  det: DetailState = {
    phase: 'opening',
    stage: 1,
    slot: 0,
    cell: 0,
    owned: false,
    ax: 0,
    ay: 0,
    as: 0,
    az: 0,
    avx: 0,
    avy: 0,
    avs: 0,
    avz: 0,
    bActive: false,
    bSlot: 0,
    bOwned: false,
    bx: 0,
    by: 0,
    bs: 0,
    bz: 0,
    bvx: 0,
    bvy: 0,
    dim: 0,
    dimV: 0,
    dragging: false,
    fx0: 0,
    fy0: 0,
    dragX: 0,
    dragY: 0,
    navDir: 0,
    axisLock: 0,
    homeX: 0,
    homeY: 0,
    homeS: 0,
    homeZ: 0,
  };

  private _aspect = 0.5;
  private pendingDown: PendingDown | null = null;
  private pendingUp: PendingUp | null = null;
  private box: CellBox = { cx: 0, cy: 0, w: 0, h: 0 };
  private cardSize = { w: 0, h: 0 };
  /** 수납 목표 칸의 world 좌표를 받는 임시 그릇. 매 프레임 도는 경로는 아니지만 할당을 안 만든다. */
  private stowBox = { x: 0, y: 0, w: 0, h: 0 };

  constructor(public input: PointerInput) {
    this.writeRS();
    this.prev.copyFrom(this.rs);
  }

  // ---------------------------------------------------------------- 좌표
  get aspect(): number {
    return this._aspect;
  }
  /**
   * 창 크기가 바뀌면 즉시 반영된다 (Renderer.onResize 가 넣어 준다).
   * 포트레이트↔펼침이 **넘기는 중에** 바뀌면 카메라 식(viewXOf)이 갈려 한 프레임 튄다 —
   * 그 세그먼트를 가던 쪽으로 즉시 확정해서 튐을 없앤다.
   */
  set aspect(v: number) {
    const was = isSpread(this._aspect);
    this._aspect = v;
    if (isSpread(v) !== was && (this.mode === 'move' || this.mode === 'settle')) this.finalizeSettle();
  }
  get screenW(): number {
    return screenWidth(this._aspect);
  }
  get W(): number {
    return faceWidth(this._aspect);
  }
  get H(): number {
    return faceHeight(this._aspect);
  }
  /** 지금의 카메라 x. 히트 테스트와 상세 보기 목표가 이걸 더한다. */
  get viewX(): number {
    return viewXOf(this.seg.kind, this.seg.dir, this.seg.u, this.face, this.W, this.spread);
  }
  /** 와이드 화면 = 양면 펼침 모드. 좌우 두 면이 동시에 보이고 카메라는 등에 고정이다. */
  get spread(): boolean {
    return isSpread(this._aspect);
  }
  /** 지금 보고 있는 펼침 번호 (0..시트 수). 포트레이트에서도 정의된다 (현재 면이 든 펼침). */
  get spreadIndex(): number {
    return spreadOf(this.face);
  }
  /** 펼침의 왼쪽 면 (0 = 왼쪽 커버 속면). 포트레이트에서는 현재 면. */
  get leftFace(): number {
    return this.spread ? spreadLeftFace(this.spreadIndex) : this.face;
  }
  /** 펼침의 오른쪽 면 (0 = 오른쪽 커버 속면). 포트레이트에서는 현재 면. */
  get rightFace(): number {
    return this.spread ? spreadRightFace(this.spreadIndex, faceCount()) : this.face;
  }
  /** 그 면이 지금 화면에 보이나. 펼침이면 좌우 둘 다, 포트레이트면 중앙 면 하나. */
  isVisibleFace(f: number): boolean {
    return f >= 1 && (f === this.leftFace || f === this.rightFace);
  }
  get faceCountNow(): number {
    return faceCount();
  }
  get theta(): number {
    return this.seg.kind === SEG_FLIP ? thetaOf(this.seg.u, this.seg.dir) : 0;
  }

  // ---------------------------------------------------------------- 진입/퇴장
  setSave(s: BinderSaveV1): void {
    this.save = s;
  }

  enter(opts: { stow?: readonly (string | null)[] | null; face?: number }): void {
    const F = faceCount();
    this.exitRequested = false;
    this.pendingDown = null;
    this.pendingUp = null;
    this.det.phase = 'opening';
    this.det.bActive = false;
    this.det.dim = 0;
    this.det.dimV = 0;
    this.resetSeg();
    this.face = clamp(Math.round(opts.face ?? this.save.lastFace ?? 1), 1, F);
    this.stow.active = false;
    this.stow.riseY = 0;
    this.stow.riseV = 0;
    this.stow.live.fill(0);
    if (opts.stow && opts.stow.length) {
      this.beginStow(opts.stow);
      this.mode = 'stow';
      this.onEvent?.('stow-start');
    } else {
      this.mode = 'idle';
    }
    this.writeRS();
    this.prev.copyFrom(this.rs);
  }

  private resetSeg(): void {
    const s = this.seg;
    s.kind = SEG_NONE;
    s.dir = 1;
    s.from = this.face;
    s.to = this.face;
    s.sheet = Math.ceil(this.face / 2);
    s.u = 0;
    s.v = 0;
    s.peeled = false;
    s.tau = 0;
    s.edgeHit = false;
    s.auto = false;
    s.autoU = 0;
  }

  /** 물리 없이 즉시 면 이동 (검증 도구 / ?face=). */
  goto(face: number): void {
    this.face = clamp(Math.round(face), 1, faceCount());
    this.mode = this.mode === 'stow' ? 'stow' : 'idle';
    this.resetSeg();
    this.save.lastFace = this.face;
    this.writeRS();
    this.prev.copyFrom(this.rs);
  }

  requestExit(): void {
    // 상세 보기 중이면 연출 없이 즉시 닫는다 — detail 상태로 나가면 다음 진입 때 카드가 공중에 뜬다.
    if (this.mode === 'detail') this.snapCloseDetail();
    this.exitRequested = true;
    audio.setPage(0);
    this.onEvent?.('exit');
  }

  // ---------------------------------------------------------------- 입력
  pointerDown(p: Vec2, t: number): void {
    this.pendingDown = { p: { x: p.x, y: p.y }, t };
  }
  pointerUp(p: Vec2, t: number, v: Vec2): void {
    this.pendingUp = { p: { x: p.x, y: p.y }, t, v: { x: v.x, y: v.y } };
  }
  clearPending(): void {
    this.pendingDown = null;
    this.pendingUp = null;
  }
  /** 손가락 아래에 칸이 있으면 접촉 틱을 울린다 (Sim 과 같은 규칙). */
  hitTestForTouchTick(p: Vec2): boolean {
    return this.mode === 'idle' && this.cellAt(p) >= 0;
  }

  private handleDown(p: Vec2, t: number): void {
    if (this.mode === 'stow') return;
    if (this.mode === 'detail') {
      const d = this.det;
      if (d.phase === 'shown' || d.phase === 'drag') {
        d.dragging = true;
        d.phase = 'drag';
        d.fx0 = p.x;
        d.fy0 = p.y;
        d.dragX = 0;
        d.dragY = 0;
        d.navDir = 0;
        d.axisLock = 0; // 새 제스처 — 축을 다시 고른다
        d.homeX = this.viewX;
        d.homeY = P.binder.detailY;
      }
      this.grab.t0 = t;
      this.grab.x0 = p.x;
      this.grab.y0 = p.y;
      return;
    }
    if (this.mode === 'settle') this.finalizeSettle();
    if (this.mode === 'move') return;
    this.mode = 'grab';
    this.grab.x0 = p.x;
    this.grab.y0 = p.y;
    this.grab.t0 = t;
    this.grab.cell = this.cellAt(p);
    // 펼침에서 반대쪽 면의 칸을 짚었으면 **그 면으로 옮긴다.** 같은 펼침 안이라 화면은 안 바뀐다
    // (렌더의 sLeft/sRight 는 2k 와 2k+1 에 같고, 카메라는 등에 고정이다).
    // 이렇게 해 두면 상세 보기·faceToSlot·cellCenter 가 전부 그대로 맞는다.
    if (this.grab.cell >= 0 && this.hitFace > 0 && this.hitFace !== this.face) this.face = this.hitFace;
    this.seg.uGrab0 = 0;
    this.seg.fx0 = p.x;
    this.onEvent?.('grab');
    // 탭이 확정되기 전에 원본 텍스처를 미리 부른다 (§6.8.6)
    if (this.grab.cell >= 0) {
      const slot = faceToSlot(this.face, this.grab.cell);
      const def = cardBySlot(slot);
      if (def && (this.save.cards[def.id] ?? 0) > 0) this.onPrefetch?.(slot);
    }
  }

  private handleUp(p: Vec2, t: number, v: Vec2): void {
    if (this.mode === 'detail') {
      this.releaseDetail(v);
      return;
    }
    if (this.mode === 'grab') {
      const dz = P.binder.dragDeadzone;
      const still = Math.abs(p.x - this.grab.x0) < dz && Math.abs(p.y - this.grab.y0) < dz;
      if (still && t - this.grab.t0 <= P.binder.tapMaxMs && this.grab.cell >= 0) {
        this.openDetail(this.grab.cell);
        return;
      }
      this.mode = 'idle';
      return;
    }
    if (this.mode === 'move') {
      // 관성 속도는 카드와 같은 경로(input.releaseVel)를 쓴다. u 축으로 환산한다.
      this.seg.v = (-this.seg.dir * v.x) / Math.max(0.5, P.binder.stepTravel);
      this.mode = 'settle';
      this.onEvent?.('release');
    }
  }

  // ---------------------------------------------------------------- 히트 테스트
  /**
   * world 좌표 p (카메라 x=0 기준) → 보이는 면의 칸 번호. 없으면 -1.
   * **펼침에서는 좌우 두 면을 다 본다.** 맞은 면은 `hitFace` 에 남는다.
   */
  cellAt(p: Vec2): number {
    const W = this.W, H = this.H;
    const bx = p.x + this.viewX; // 루트가 -viewX 로 밀려 있다 (§12 함정)
    const ly = p.y - P.binder.faceY;
    const F = faceCount();
    let n = 1;
    this.hitCand[0] = this.face;
    if (this.spread) {
      this.hitCand[0] = this.leftFace;
      this.hitCand[1] = this.rightFace;
      n = 2;
    }
    for (let j = 0; j < n; j++) {
      const f = this.hitCand[j];
      if (f < 1 || f > F) continue;
      const lx = bx - faceCenterX(f, W);
      const back = faceIsBack(f);
      for (let i = 0; i < CELLS_PER_FACE; i++) {
        const b = cellBox(i, back, W, H, this.box);
        if (Math.abs(lx - b.cx) <= b.w / 2 && Math.abs(ly - b.cy) <= b.h / 2) {
          this.hitFace = f;
          return i;
        }
      }
    }
    this.hitFace = 0;
    return -1;
  }

  cellCenter(cell: number, out: { x: number; y: number; w: number; h: number }, face = this.face): void {
    const W = this.W, H = this.H;
    const b = cellBox(cell, faceIsBack(face), W, H, this.box);
    out.x = faceCenterX(face, W) + b.cx;
    out.y = P.binder.faceY + b.cy;
    out.w = b.w;
    out.h = b.h;
  }

  countOfSlot(slot: number): number {
    const def = cardBySlot(slot);
    return def ? this.save.cards[def.id] ?? 0 : 0;
  }

  // ---------------------------------------------------------------- 스텝
  snapshot(): void {
    this.prev.copyFrom(this.rs);
  }

  step(h: number, simT: number): void {
    this.time += h;
    if (this.pendingDown && this.pendingDown.t <= simT + 0.5) {
      this.handleDown(this.pendingDown.p, this.pendingDown.t);
      this.pendingDown = null;
    }
    if (this.pendingUp && this.pendingUp.t <= simT + 0.5) {
      this.handleUp(this.pendingUp.p, this.pendingUp.t, this.pendingUp.v);
      this.pendingUp = null;
    }

    switch (this.mode) {
      case 'stow':
        this.stepStow(h);
        break;
      case 'grab':
        this.stepGrab();
        break;
      case 'move':
        this.stepTurn(h, true);
        break;
      case 'settle':
        this.stepTurn(h, false);
        break;
      case 'detail':
        this.stepDetail(h);
        break;
      default:
        audio.setPage(0);
        break;
    }
    this.writeRS();
  }

  // ---------------------------------------------------------------- 넘기기
  private stepGrab(): void {
    const dx = this.input.pos.x - this.grab.x0;
    if (Math.abs(dx) < P.binder.dragDeadzone) return;
    const dir = dx < 0 ? 1 : -1; // 왼쪽으로 끌면 다음
    const s = this.seg;
    s.dir = dir;
    s.from = this.face;
    s.u = 0;
    s.v = 0;
    s.peeled = false;
    s.uGrab0 = 0;
    s.fx0 = this.grab.x0;
    s.edgeHit = false;
    s.lastSlap = -1;
    if (this.spread) this.beginSpreadSeg(dir);
    else this.beginFaceSeg(dir);
  }

  /** 포트레이트 — 한 면씩. 홀짝이 flip/pan 을 정한다 (§6.3.1). **이 동작은 바뀌지 않았다.** */
  private beginFaceSeg(dir: number): void {
    const F = faceCount();
    const s = this.seg;
    if ((dir > 0 && this.face >= F) || (dir < 0 && this.face <= 1)) {
      s.kind = SEG_EDGE;
      s.to = this.face;
      this.mode = 'move';
      this.onEvent?.('edge');
      return;
    }
    s.to = this.face + dir;
    // 홀짝이 정한다 (§6.3.1). 홀수면 다음 = flip, 짝수면 다음 = pan. 이전은 정확히 반대.
    const odd = this.face % 2 === 1;
    const isFlip = odd ? dir > 0 : dir < 0;
    if (isFlip) {
      s.kind = SEG_FLIP;
      s.sheet = odd ? (this.face + 1) / 2 : this.face / 2;
      this.onEvent?.('move-flip');
    } else {
      s.kind = SEG_PAN;
      s.sheet = Math.ceil(this.face / 2);
      this.onEvent?.('move-pan');
    }
    this.mode = 'move';
  }

  /**
   * 펼침 — 펼침 단위로 넘긴다. **항상 flip 이다** (pan 이 없다):
   *   다음 (k → k+1) = 시트 k+1 을 왼쪽으로 뒤집는다 (θ 0→π)
   *   이전 (k → k-1) = 시트 k 를 오른쪽으로 되뒤집는다 (θ π→0)
   * **물리는 포트레이트의 stepFlip 을 그대로 쓴다** — 새 물리를 만들지 않았다.
   *
   * 어느 쪽 절반을 짚었는지는 **보지 않는다.** 넘길 수 있는 시트가 방향 하나로 정해지기
   * 때문이다 (왼쪽 면을 더 왼쪽으로는 갈 곳이 없고, 오른쪽 면을 더 오른쪽으로도 없다).
   * 그래서 "왼쪽 면을 오른쪽으로 쓸면 이전 / 오른쪽 면을 왼쪽으로 쓸면 다음" 이 그대로 성립하고,
   * 반대 방향 드래그도 같은 방향 규칙으로 받아 준다 (포트레이트와 손이 같아진다).
   */
  private beginSpreadSeg(dir: number): void {
    const F = faceCount();
    const S = F / 2; // 시트 수. 펼침은 0..S 라 S+1 개다
    const k = this.spreadIndex;
    const kTo = k + dir;
    const s = this.seg;
    if (kTo < 0 || kTo > S) {
      s.kind = SEG_EDGE;
      s.to = this.face;
      this.mode = 'move';
      this.onEvent?.('edge');
      return;
    }
    s.kind = SEG_FLIP;
    s.sheet = dir > 0 ? k + 1 : k;
    s.to = spreadCanonFace(kTo, F);
    this.mode = 'move';
    this.onEvent?.('move-flip');
  }

  /** 손가락의 화면 이동 거리 → 목표 u. 카메라가 시트를 따라가므로 점 대응은 정의되지 않는다 (§6.3.5). */
  private targetU(): number {
    const s = this.seg;
    // 수납 자동 넘기기 — 손가락이 없으니 가상 목표를 쓴다. 이 한 줄이 자동/수동의 유일한 차이다.
    if (s.auto) return clamp(s.autoU, 0, 1);
    return clamp(s.uGrab0 - (s.dir * (this.input.pos.x - s.fx0)) / Math.max(0.5, P.binder.stepTravel), 0, 1);
  }

  private stepTurn(h: number, grabbing: boolean): void {
    switch (this.seg.kind) {
      case SEG_FLIP:
        this.stepFlip(h, grabbing);
        break;
      case SEG_PAN:
        this.stepPan(h, grabbing);
        break;
      case SEG_EDGE:
        this.stepEdge(h, grabbing);
        break;
      default:
        this.mode = 'idle';
        break;
    }
  }

  private stepFlip(h: number, grabbing: boolean): void {
    const b = P.binder;
    const s = this.seg;
    const I = Math.max(0.001, b.inertia);
    const sign = s.dir > 0 ? 1 : -1;
    let th = thetaOf(s.u, s.dir);
    let om = sign * Math.PI * s.v;

    let tf = 0;
    // 수납의 자동 넘기기도 **끌리는 쪽**이다 (손가락 대신 가상 목표). 착지 판정은 아래 !grabbing 이 한다.
    if (grabbing || s.auto) {
      const thT = thetaOf(this.targetU(), s.dir);
      tf = b.grabStiffness * (thT - th) - 2 * b.grabDamping * Math.sqrt(b.grabStiffness * I) * om;
      s.tau = tf;
      if (!s.peeled) {
        // 뜯김 — 슬리브가 서로 살짝 붙어 있다. **한 번만.** 반복 stick-slip 은 뭉치를 미는 느낌이 된다.
        if (Math.abs(tf) < b.peelTorque) {
          s.v = 0;
          audio.setPage(0);
          return;
        }
        s.peeled = true;
        audio.clickRelease(Math.min(1, Math.abs(tf) / Math.max(0.01, b.peelTorque) - 1));
        haptics.pulse(P.haptic.slip);
        this.onEvent?.('peel');
      }
    } else {
      s.tau = 0;
    }

    // 중력 능선: cos(θ + tilt) = 0 인 각을 넘기면 스스로 넘어간다. 속도로 분기하지 않는다.
    const tg = b.gravity * Math.cos(th + b.tilt);
    om = (om + ((tf - tg) / I) * h) / (1 + (b.hingeFriction / I) * h);
    const dry = (b.hingeDryFriction / I) * h;
    if (Math.abs(om) <= dry) om = 0;
    else om -= Math.sign(om) * dry;
    om = clamp(om, -b.maxOmega, b.maxOmega);
    th += om * h;

    let hitEnd = false;
    if (th <= 0) {
      if (om < -0.6) hitEnd = true;
      th = 0;
      om = om < 0 ? -om * b.landBounce : om;
    } else if (th >= Math.PI) {
      if (om > 0.6) hitEnd = true;
      th = Math.PI;
      om = om > 0 ? -om * b.landBounce : om;
    }
    if (hitEnd && this.time - s.lastSlap > 0.12) {
      s.lastSlap = this.time;
      audio.pageSlap(Math.min(1, Math.abs(om) / 8 + 0.35));
      haptics.pulse(P.haptic.land);
    }

    s.u = clamp(s.dir > 0 ? th / Math.PI : 1 - th / Math.PI, 0, 1);
    s.v = (sign * om) / Math.PI;
    audio.setPage(Math.abs(om) * (this.W / 2));

    if (!grabbing) {
      const atEnd = th <= 1e-6 || th >= Math.PI - 1e-6;
      // **자동(수납)은 되돌아오지 않는다.** 가상 손가락이 아직 초반이라 토크가 중력을 못 이기는
      // 동안 θ=0 에 붙어 있는데, 거기서 land(false) 를 해 버리면 같은 세그먼트를 무한히 다시 연다
      // (목표 면은 그대로이므로). 끝까지 넘어간 경우에만 착지시킨다 — u 는 계속 1 로 끌리므로
      // 언젠가 반드시 넘어간다.
      if (atEnd && Math.abs(om) < 0.45 && !(s.auto && s.u <= 0.5)) this.land(s.u > 0.5);
    }
  }

  private stepPan(h: number, grabbing: boolean): void {
    const b = P.binder;
    const s = this.seg;
    if (grabbing) {
      // 마찰 접촉 — 1:1 로 따라온다. 스프링 없음.
      const ut = this.targetU();
      s.u = ut;
      // 속도는 **평활된 손가락 속도**에서 가져온다. (ut - u)/h 는 포인터 샘플이 있는 스텝에만
      // 값이 튀어서 소리가 한 프레임짜리 딸꾹질이 된다 (flip 은 물리 각속도라 연속이다).
      s.v = (-s.dir * this.input.vSlow.x) / Math.max(0.5, P.binder.stepTravel);
      audio.setPage(Math.abs(s.v) * this.W * b.panSoundScale);
      return;
    }
    // 자동(수납)은 무조건 다음 면까지 간다 — 손을 놓은 뒤의 스냅 스프링을 그대로 탄다.
    // (그래서 `stowTurnTime` 은 flip 에만 걸린다. pan 은 실물로도 "밀어 놓으면 붙는" 동작이라
    //  가상 손가락으로 끌 이유가 없다. 속도는 panSnap / panDamping 이 정한다.)
    const target = s.auto ? 1 : Math.abs(s.v) >= b.panFlick ? (s.v > 0 ? 1 : 0) : Math.round(s.u);
    const w = b.panSnap;
    s.v += (w * w * (target - s.u) - 2 * b.panDamping * w * s.v) * h;
    s.u += s.v * h;
    audio.setPage(Math.abs(s.v) * this.W * b.panSoundScale);
    if (Math.abs(s.u - target) < 0.004 && Math.abs(s.v) < 0.06) {
      s.u = target;
      s.v = 0;
      audio.thud(0.25);
      haptics.pulse(P.haptic.slip);
      this.land(target === 1, target === 1 ? 'snap' : 'return');
    }
  }

  private stepEdge(h: number, grabbing: boolean): void {
    const b = P.binder;
    const s = this.seg;
    let target = 0;
    if (grabbing) {
      const ut = this.targetU();
      target = Math.min(ut, b.edgeLimit);
      if (ut > b.edgeLimit && !s.edgeHit) {
        s.edgeHit = true;
        audio.thud(0.3);
        haptics.pulse(P.haptic.land);
      }
    }
    const w = Math.sqrt(Math.max(1, b.edgeStiffness));
    s.v += (b.edgeStiffness * (target - s.u) - 2 * w * s.v) * h;
    s.u += s.v * h;
    audio.setPage(0);
    if (!grabbing && Math.abs(s.u) < 0.004 && Math.abs(s.v) < 0.06) {
      s.u = 0;
      s.v = 0;
      this.land(false);
    }
  }

  private land(arrived: boolean, name?: string): void {
    const s = this.seg;
    if (arrived && s.kind !== SEG_EDGE) {
      this.face = clamp(s.to, 1, faceCount());
      if (s.kind === SEG_FLIP) {
        audio.pageSlap(0.5);
        haptics.pulse(P.haptic.land);
      }
    }
    this.onEvent?.(name ?? (arrived ? 'land' : 'return'));
    const stowing = this.stow.active;
    this.resetSeg();
    // **수납 중이면 계속 수납이다.** idle 로 떨어뜨리면 남은 카드가 안 들어가고 입력이 열린다.
    this.mode = stowing ? 'stow' : 'idle';
    audio.setPage(0);
    if (this.save.lastFace !== this.face) {
      this.save.lastFace = this.face;
      // 면이 정착할 때만 쓴다. 드래그 중에는 쓰지 않는다 (§4.4).
      // 수납은 한 번에 여러 면을 넘기므로 넘길 때마다 쓰지 않고 끝나고 한 번만 쓴다 (stowEnd).
      if (!stowing) saveBinder(this.save);
    }
  }

  /** settle 중에 손가락이 다시 닿았을 때 — 가던 쪽으로 즉시 확정하고 새 세그먼트를 받는다. */
  private finalizeSettle(): void {
    const s = this.seg;
    if (s.kind === SEG_NONE) return;
    this.land(s.kind !== SEG_EDGE && s.u > 0.5);
  }

  // ---------------------------------------------------------------- 수납
  /**
   * 한 팩을 수납한다. **슬롯 순서로 정렬해 한 장씩** 처리한다 (2026-09-16 사용자 요청).
   *
   * 정렬이 곧 연출의 뼈대다: 슬롯이 오름차순이면 면도 오름차순이라 바인더가 **한 방향으로만**
   * 넘어간다. 앞뒤로 왔다 갔다 하면 "어디에 꽂혔는지" 가 안 읽힌다.
   *
   * 레지스트리에 없는 id(그 등급에 카드가 0장이면 null 이다)는 여기서 버린다 — 커밋에서도
   * 안 세어진 카드라, 연출에만 나오면 배지 숫자와 어긋난다.
   */
  private beginStow(ids: readonly (string | null)[]): void {
    const st = this.stow;
    st.active = true;
    st.t = 0;
    st.ct = 0;
    st.phase = 'rise';
    st.cur = 0;
    st.turned = false;
    st.riseY = -(this.H + 4);
    st.riseV = 0;
    st.started.fill(0);
    st.landed.fill(0);
    st.live.fill(0);
    st.slot.fill(0);
    st.face.fill(0);

    const slots: number[] = [];
    for (let i = 0; i < ids.length && slots.length < CARD_COUNT; i++) {
      const id = ids[i];
      const d = id ? CARD_BY_ID.get(id) : undefined;
      if (d) slots.push(d.slot);
    }
    slots.sort((a, b) => a - b);
    st.n = slots.length;
    for (let i = 0; i < st.n; i++) {
      st.live[i] = 1;
      st.slot[i] = slots[i];
      st.face[i] = slotToFace(slots[i]);
    }
    // **첫 카드의 면에서 시작한다.** enter() 가 받은 face 보다 이쪽이 우선이다 —
    // 첫 장부터 페이지를 넘기게 하면 "바인더가 왜 움직이지" 로만 읽힌다.
    if (st.n > 0) this.face = clamp(st.face[0], 1, faceCount());
    this.resetSeg();
  }

  /** 카드 한 장 차례 시작. 면이 이미 보이면 바로 떨어뜨리고, 아니면 그 면까지 넘긴다. */
  private stowBeginCard(i: number): void {
    const st = this.stow;
    st.ct = 0;
    st.turned = false;
    if (this.isVisibleFace(st.face[i])) this.stowBeginDrop(i);
    else st.phase = 'turn';
  }

  /** 목표 칸과 출발점을 잡고 낙하를 시작한다. **면이 보이는 상태에서만** 불린다. */
  private stowBeginDrop(i: number): void {
    const st = this.stow;
    const face = st.face[i];
    const cell = (st.slot[i] - 1) % CELLS_PER_FACE;
    // 칸 좌표는 binder-layout 에서만 나온다 (sim 과 render 가 같은 함수를 쓴다 — §12 함정).
    this.cellCenter(cell, this.stowBox, face);
    cellBox(cell, faceIsBack(face), this.W, this.H, this.box);
    cardSizeInCell(this.box, CARD_ASPECT, this.cardSize);
    st.tx[i] = this.stowBox.x;
    st.ty[i] = this.stowBox.y;
    st.ts[i] = this.cardSize.w;
    // **칸 바로 위에서 아래로 꽂는다** (2026-09-16 사용자 요청).
    // 예전엔 -PI/2 방향, 즉 화면 아래에서 비스듬히 날아 올라왔다. 실물은 슬리브 입구가
    // 위에 있으니 위에서 내려와야 "꽂는다" 로 읽힌다. x 는 목표 칸에 맞추고 y 만 띄운다.
    st.x[i] = this.stowBox.x;
    st.y[i] = this.stowBox.y + P.binder.stowStartDist;
    st.s[i] = WORLD.cardW * 0.5;
    st.vx[i] = st.vy[i] = st.vs[i] = 0;
    st.started[i] = 1;
    st.ct = 0;
    st.phase = 'drop';
  }

  private stowLand(i: number): void {
    const st = this.stow;
    st.landed[i] = 1;
    audio.thud(0.5);
    haptics.pulse(P.haptic.land);
    this.onEvent?.('stow-land');
    st.ct = 0;
    st.phase = 'gap';
  }

  private stowNext(): void {
    const st = this.stow;
    st.cur++;
    if (st.cur >= st.n) this.stowEnd();
    else this.stowBeginCard(st.cur);
  }

  private stowEnd(): void {
    const st = this.stow;
    for (let i = 0; i < CARD_COUNT; i++) if (st.live[i]) st.landed[i] = 1;
    st.active = false;
    this.resetSeg();
    this.mode = 'idle';
    audio.setPage(0);
    // 넘기는 동안 land() 가 저장을 건너뛰었다 (§4.4). 도착한 면을 여기서 한 번 쓴다.
    if (this.save.lastFace !== this.face) {
      this.save.lastFace = this.face;
      saveBinder(this.save);
    }
    this.onEvent?.('stow-end');
  }

  private stepStow(h: number): void {
    const b = P.binder;
    const st = this.stow;
    st.t += h;
    st.ct += h;
    // 바인더가 화면 아래에서 올라온다. 카드 차례와 무관하게 계속 돈다.
    const rw = b.stowRiseSpeed;
    st.riseV += (rw * rw * (0 - st.riseY) - 2 * 1.05 * rw * st.riseV) * h;
    st.riseY += st.riseV * h;

    switch (st.phase) {
      case 'rise':
        // 꽂을 카드가 하나도 없으면(전부 레지스트리 밖) 연출할 것이 없다.
        if (st.n <= 0) this.stowEnd();
        else if (st.ct >= b.stowDelay) this.stowBeginCard(0);
        break;
      case 'turn':
        this.stepStowTurn(h);
        break;
      case 'pause':
        if (st.ct >= b.stowTurnPause) this.stowBeginDrop(st.cur);
        break;
      case 'drop':
        this.stepStowDrop(h);
        break;
      case 'gap':
        if (st.ct >= b.stowGap) this.stowNext();
        break;
    }
  }

  /**
   * 목표 면까지 **한 면씩** 넘긴다. 새 애니메이션이 아니라 손으로 넘길 때와 **같은 세그먼트**를
   * 가상 손가락(`seg.auto`)으로 미는 것이다 — 홀짝에 따라 flip 은 flip 대로, pan 은 pan 대로 돈다.
   * 뜯김 클릭·능선·착지 소리·진동이 전부 그대로 나온다.
   */
  private stepStowTurn(h: number): void {
    const st = this.stow;
    const s = this.seg;
    const target = clamp(st.face[st.cur], 1, faceCount());
    if (s.kind === SEG_NONE) {
      if (this.isVisibleFace(target)) {
        // 도착. 실제로 넘겼을 때만 뜸을 준다 — 처음부터 그 면이었으면 기다릴 이유가 없다.
        st.ct = 0;
        if (st.turned) st.phase = 'pause';
        else this.stowBeginDrop(st.cur);
        return;
      }
      st.turned = true;
      st.ct = 0;
      const dir = target > this.face ? 1 : -1;
      if (this.spread) this.beginSpreadSeg(dir);
      else this.beginFaceSeg(dir);
      s.auto = true;
      s.autoU = 0;
      s.uGrab0 = 0;
      // begin*Seg 가 mode 를 'move' 로 바꾼다. 수납은 계속 수납이어야 입력이 안 열린다.
      this.mode = 'stow';
      // 끝 면에 걸렸다면(SEG_EDGE) 물리로는 도달할 수 없다. 즉시 맞춰 놓고 진행한다.
      // (begin*Seg 가 방금 kind 를 바꿨다. `s.kind` 로 읽으면 위 SEG_NONE 분기의 좁혀진 타입이 남는다.)
      const started: SegKindId = this.seg.kind;
      if (started === SEG_EDGE) {
        this.face = target;
        this.resetSeg();
      }
      return;
    }
    // 가상 손가락이 stowTurnTime 에 걸쳐 목표 u 를 0 → 1 로 끈다. 시트는 제 관성으로 따라온다.
    s.autoU = Math.min(1, s.autoU + h / Math.max(0.02, P.binder.stowTurnTime));
    this.stepTurn(h, false);
    // 안전장치 — 한 번의 넘기기가 이 시간을 넘기면 물리를 포기하고 그 면으로 맞춘다.
    // **단계마다** 적용된다. 목표 면이 멀면 넘기기가 여러 번이므로 전체 시간은 이보다 길어진다.
    if (st.ct > P.binder.stowDuration) {
      this.face = clamp(s.to, 1, faceCount());
      this.resetSeg();
      this.mode = 'stow';
      st.ct = 0;
    }
  }

  /** 임계감쇠 스프링 (zeta = 1). 이징 커브 금지 규칙에 맞춰 스프링만 쓴다. */
  private stepStowDrop(h: number): void {
    const b = P.binder;
    const st = this.stow;
    const i = st.cur;
    const w = b.stowSpeed;
    st.vx[i] += (w * w * (st.tx[i] - st.x[i]) - 2 * w * st.vx[i]) * h;
    // riseY 를 여기서 더하면 **이중 적용**이다 — 렌더의 root.position.y 가 이미 riseY 다
    // (binder-render `this.root.add(m)`). 더하면 첫 장이 바인더 상승분만큼 더 아래에서
    // 출발해 "아래에서 위로 꽂힌다" 로 보인다 (2026-09-16 사용자 보고 2회).
    st.vy[i] += (w * w * (st.ty[i] - st.y[i]) - 2 * w * st.vy[i]) * h;
    st.vs[i] += (w * w * (st.ts[i] - st.s[i]) - 2 * w * st.vs[i]) * h;
    st.x[i] += st.vx[i] * h;
    st.y[i] += st.vy[i] * h;
    st.s[i] += st.vs[i] * h;
    const d = Math.hypot(st.tx[i] - st.x[i], st.ty[i] - st.y[i]);
    const sp = Math.hypot(st.vx[i], st.vy[i]);
    // 정착 판정은 stepOpen() 과 같은 기준이다 (거리 0.03 unit / 속도 0.4 unit/s).
    if ((d < 0.03 && sp < 0.4) || st.ct > b.stowDuration) this.stowLand(i);
  }

  // ---------------------------------------------------------------- 상세 보기
  private detailTargetWidth(): number {
    // detailWidthFrac 은 **화면상 폭**이다. z 가 0 이 아니라 커 보이므로 배율로 나눠 메시 폭을 정한다.
    const k = perspScale(this.detailZ());
    const byW = (P.binder.detailWidthFrac * this.screenW) / k;
    // 폭 비만 보면 **가로로 넓은 화면에서 카드가 위아래로 넘친다** (화면 폭이 viewH 의 몇 배가 되므로).
    // 높이로도 맞춘다 — faceWidth 의 fitH 와 같은 이유다. 세로 폰에서는 이 제약이 안 문다
    // (390×844 에서 카드 높이가 화면의 53% 라 0.86 에 한참 못 미친다) → 포트레이트 동작은 그대로다.
    const byH = (P.binder.detailFitH * WORLD.viewH) / k / CARD_ASPECT;
    return Math.min(byW, byH);
  }
  /**
   * 상세 카드의 z. **여는 순간부터 닫힘이 끝날 때까지 이 값에서 안 내려온다** (§9, §12).
   * z 는 손맛의 축이 아니라 **가시성**의 축이다 — 지각되는 움직임은 x/y/크기이고 z 는 어느 레이어냐일 뿐이다.
   * 스프링으로 올리면 초반 프레임에서 카드가 슬리브 앞판(gloss)·디밍 평면(dim) **뒤**에 깔린다.
   * 그래서 스냅한다. 최소한 dim 위여야 선택된 카드가 어두워지지 않는다.
   */
  private detailZ(): number {
    return Math.max(P.binder.detailLift, BINDER_LAYERS.dim + 0.05);
  }

  private openDetail(cell: number): void {
    const slot = faceToSlot(this.face, cell);
    const def = cardBySlot(slot);
    if (!def) {
      this.mode = 'idle';
      return; // 레지스트리 범위 밖의 빈 칸은 열리지 않는다
    }
    // **아직 안 가진 칸도 열지 않는다** — 열면 카드 뒷면만 떠서 버그로 보인다
    // (2026-09-19 사용자 보고). 레지스트리에 있다는 것과 가졌다는 것은 다르다.
    if ((this.save.cards[def.id] ?? 0) <= 0) {
      this.mode = 'idle';
      return;
    }
    const d = this.det;
    const box = { x: 0, y: 0, w: 0, h: 0 };
    this.cellCenter(cell, box);
    cellBox(cell, faceIsBack(this.face), this.W, this.H, this.box);
    cardSizeInCell(this.box, CARD_ASPECT, this.cardSize);
    d.slot = slot;
    d.cell = cell;
    d.owned = (this.save.cards[def.id] ?? 0) > 0;
    d.phase = 'opening';
    d.stage = 1;
    d.ax = box.x;
    d.ay = box.y;
    d.as = this.cardSize.w;
    d.az = this.detailZ(); // 즉시 스냅 — 슬리브 앞판·디밍 위에서 시작해야 "위로 빠져나오는" 것으로 보인다
    d.avx = d.avy = d.avs = d.avz = 0;
    d.bActive = false;
    d.dragging = false;
    d.dragX = d.dragY = 0;
    d.navDir = 0;
    d.axisLock = 0;
    this.mode = 'detail';
    audio.setFriction(6);
    haptics.pulse(P.haptic.slip);
    this.onEvent?.('detail-open');
  }

  private cellHome(cell: number, out: { x: number; y: number; w: number; h: number }): number {
    this.cellCenter(cell, out);
    cellBox(cell, faceIsBack(this.face), this.W, this.H, this.box);
    cardSizeInCell(this.box, CARD_ASPECT, this.cardSize);
    return this.cardSize.w;
  }

  private stepDetail(h: number): void {
    const b = P.binder;
    const d = this.det;
    const w = b.detailSpeed;
    const z = b.detailDamping;
    const box = { x: 0, y: 0, w: 0, h: 0 };
    const cellW = this.cellHome(d.cell, box);

    // 디밍
    const dimTarget = d.phase === 'closing' ? 0 : b.detailDim;
    d.dimV += (w * w * (dimTarget - d.dim) - 2 * w * d.dimV) * h;
    d.dim = clamp(d.dim + d.dimV * h, 0, 1);

    let tx = this.viewX, ty = b.detailY, ts = this.detailTargetWidth(), tz = this.detailZ();
    const pullY = box.y + b.detailPull * box.h;

    if (d.phase === 'opening') {
      if (d.stage === 1) {
        // 1단계: 슬리브 위쪽 입구로 빠져나온다. z 는 openDetail 에서 이미 스냅했으므로 **건드리지 않는다**
        // (여기서 낮은 값을 주면 스프링이 카드를 앞판 뒤로 끌어내린다 — 이게 "뒤에서 나오는" 버그였다).
        tx = box.x;
        ty = pullY;
        ts = cellW;
        if (Math.hypot(d.ax - tx, d.ay - ty) < 0.05) d.stage = 2;
      }
      this.springTo(d, tx, ty, ts, tz, w, z, h);
      audio.setFriction(Math.hypot(d.avx, d.avy));
      if (d.stage === 2 && Math.hypot(d.ax - tx, d.ay - ty) < 0.02 && Math.hypot(d.avx, d.avy) < 0.3) {
        d.phase = 'shown';
        audio.setFriction(0);
        this.onEvent?.('detail-shown');
      }
      return;
    }

    if (d.phase === 'shown') {
      this.springTo(d, tx, ty, ts, tz, w, z, h);
      audio.setFriction(0);
      return;
    }

    if (d.phase === 'drag') {
      // 잡고 있는 물건 — 스프링 없이 1:1
      d.dragX = this.input.pos.x - d.fx0;
      d.dragY = this.input.pos.y - d.fy0;
      const lastDir = d.navDir;
      // **축은 한 번만 정한다.** 매 프레임 재판정하면 대각선으로 끌 때 가로↔세로가
      // 오가며 y 가 붙었다 떨어졌다 해 흔들린다 (2026-09-19). 데드존을 넘는 순간의
      // 우세 축으로 잠그고, 손을 뗄 때까지 유지한다 — 일반적인 스크롤 뷰와 같다.
      if (d.axisLock === 0) {
        const far = Math.max(Math.abs(d.dragX), Math.abs(d.dragY));
        if (far >= b.dragDeadzone) d.axisLock = Math.abs(d.dragX) > Math.abs(d.dragY) ? 1 : 2;
      }
      d.navDir = d.axisLock === 1 ? (d.dragX < 0 ? 1 : -1) : 0;
      // 끝 판정은 **칸이 아니라 보유 카드 기준**이다. 칸으로 보면 빈 칸이 이웃일 때도
      // 넘어가 버려 뒷면이 뜬다 (2026-09-19). 다음 보유 카드가 없으면 그게 끝이다.
      const nextSlot = d.navDir !== 0 ? this.nextOwnedSlot(d.slot, d.navDir) : 0;
      const atEdge = d.navDir !== 0 && nextSlot === 0;
      let dx = d.dragX;
      if (d.navDir !== 0 && atEdge) dx = clamp(dx, -b.detailNavDist * b.detailNavEdge, b.detailNavDist * b.detailNavEdge);
      d.ax = this.viewX + dx;
      // **가로로 넘기는 중에는 y 를 고정한다.** 손가락을 따라 위아래로 흔들리면
      // "좌우 스크롤" 이 아니라 물건을 들고 흔드는 느낌이 된다 (2026-09-19 사용자 요청).
      // 세로 드래그(navDir === 0)는 닫기 제스처라 그대로 따라가야 한다.
      d.ay = d.navDir !== 0 ? b.detailY : b.detailY + d.dragY;
      d.as = ts;
      d.az = tz;
      d.avx = d.avy = 0;
      // 카루셀 — 이웃 카드가 screenW 만큼 떨어져 같이 따라온다
      if (d.navDir !== 0 && !atEdge) {
        if (!d.bActive || lastDir !== d.navDir) this.prepareBSlot(nextSlot);
        d.bx = d.ax + d.navDir * this.screenW;
        d.by = b.detailY; // 이웃도 같은 높이 — 좌우 스크롤이므로 y 는 안 움직인다
        d.bs = ts;
        d.bz = tz;
      } else {
        d.bActive = false;
      }
      audio.setFriction(Math.min(12, Math.hypot(this.input.vSlow.x, this.input.vSlow.y) * 0.5));
      return;
    }

    if (d.phase === 'nav') {
      const targetAx = this.viewX - d.navDir * this.screenW;
      this.springTo(d, targetAx, ty, ts, tz, w, z, h);
      d.bvx += (w * w * (this.viewX - d.bx) - 2 * z * w * d.bvx) * h;
      d.bvy += (w * w * (ty - d.by) - 2 * z * w * d.bvy) * h;
      d.bx += d.bvx * h;
      d.by += d.bvy * h;
      d.bs = ts;
      d.bz = tz;
      audio.setFriction(Math.min(12, Math.abs(d.bvx)));
      if (Math.abs(d.bx - this.viewX) < 0.02 && Math.abs(d.bvx) < 0.3) {
        // A/B 역할 교대
        d.slot = d.bSlot;
        // **칸은 새 슬롯에서 유도한다.** cell += navDir 은 면을 넘어갈 때 9 를 넘거나
        // 음수가 되어 닫힘 애니메이션이 엉뚱한 칸으로 빨려 들어간다 (2026-09-19).
        d.cell = slotToCell(d.slot);
        // **면은 여기서 옮기지 않는다.** viewX 가 face 의 함수(포트레이트는 ±W/2)라
        // 여기서 바꾸면 카드 목표 x 가 한 면 폭만큼 점프해 카드가 튕겼다 돌아온다
        // (2026-09-19 사용자 보고: "위아래로 움직이고 갔다가 되돌아옴").
        // 실제 면 이동은 닫을 때(beginCloseDetail) 한다 — 그때는 카드가 칸으로
        // 빨려 들어가야 하므로 좌표가 어차피 다시 잡힌다.
        d.owned = d.bOwned;
        d.ax = d.bx;
        d.ay = d.by;
        d.as = d.bs;
        d.az = d.bz;
        d.avx = d.avy = d.avs = d.avz = 0;
        d.bActive = false;
        d.phase = 'shown';
        haptics.pulse(P.haptic.slip);
        audio.setFriction(0);
        this.onEvent?.('detail-nav');
      }
      return;
    }

    // closing — 열림의 역순
    if (d.stage === 2) {
      tx = box.x;
      ty = pullY;
      ts = cellW;
      this.springTo(d, tx, ty, ts, tz, w, z, h); // tz 는 detailZ() 그대로 — 닫는 동안에도 앞판 위에 있어야 한다
      if (Math.hypot(d.ax - tx, d.ay - ty) < 0.05) d.stage = 1;
    } else {
      tx = box.x;
      ty = box.y;
      ts = cellW;
      // 칸에 완전히 들어간 **뒤에** 내린다. 도중에 내리면 같은 버그가 반대로 난다 (카드가 앞판 뒤로 잠겨 들어간다).
      this.springTo(d, tx, ty, ts, tz, w, z, h);
      if (Math.hypot(d.ax - tx, d.ay - ty) < 0.02 && Math.hypot(d.avx, d.avy) < 0.3) {
        audio.thud(0.3);
        haptics.pulse(P.haptic.land);
        audio.setFriction(0);
        this.mode = 'idle';
        d.dim = 0;
        d.dimV = 0;
        d.az = BINDER_LAYERS.card; // 여기서 비로소 내린다 — 이 프레임부터 상세 카드는 안 그려진다
        d.avz = 0;
        this.onEvent?.('detail-closed');
        return;
      }
    }
    audio.setFriction(Math.hypot(d.avx, d.avy));
  }

  /** 이웃 카드(B)를 **슬롯으로** 준비한다. 칸 기반이던 것을 슬롯 기반으로 바꿨다 (2026-09-19). */
  private prepareBSlot(slot: number): void {
    const d = this.det;
    const def = cardBySlot(slot);
    d.bActive = true;
    d.bSlot = slot;
    d.bOwned = !!def && (this.save.cards[def.id] ?? 0) > 0;
    d.bvx = 0;
    d.bvy = 0;
    if (def && d.bOwned) this.onPrefetch?.(slot);
  }

  /** 슬롯을 실제로 가지고 있는가. 빈 칸은 열지도, 훑지도 않는다 (2026-09-19). */
  private slotOwned(slot: number): boolean {
    const def = cardBySlot(slot);
    return !!def && (this.save.cards[def.id] ?? 0) > 0;
  }

  /**
   * **보유한** 카드 중 `from` 에서 dir 방향으로 다음 것의 슬롯. 없으면 0.
   *
   * 칸(cell) 을 ±1 하면 225종 중 몇 장만 가진 초반에 **빈 칸만 계속 지나간다**
   * (2026-09-19 사용자 보고). 도감 순서 = 슬롯 오름차순이므로 슬롯을 훑되
   * 보유한 것만 고른다. 면을 넘어가도 이어진다.
   */
  private nextOwnedSlot(from: number, dir: number): number {
    const last = maxAssignedSlot();
    for (let s = from + dir; s >= 1 && s <= last; s += dir) {
      if (this.slotOwned(s)) return s;
    }
    return 0;
  }

  private springTo(d: DetailState, tx: number, ty: number, ts: number, tz: number, w: number, z: number, h: number): void {
    d.avx += (w * w * (tx - d.ax) - 2 * z * w * d.avx) * h;
    d.avy += (w * w * (ty - d.ay) - 2 * z * w * d.avy) * h;
    d.avs += (w * w * (ts - d.as) - 2 * z * w * d.avs) * h;
    d.avz += (w * w * (tz - d.az) - 2 * z * w * d.avz) * h;
    d.ax += d.avx * h;
    d.ay += d.avy * h;
    d.as += d.avs * h;
    d.az += d.avz * h;
  }

  private releaseDetail(v: Vec2): void {
    const b = P.binder;
    const d = this.det;
    if (d.phase !== 'drag') {
      // 탭으로 닫기 (카드 위든 밖이든)
      if (d.phase === 'shown' || d.phase === 'opening') this.beginCloseDetail();
      return;
    }
    d.dragging = false;
    const navOk = Math.abs(d.dragX) >= b.detailNavDist || Math.abs(v.x) >= b.detailFlick;
    const closeOk = d.dragY <= -b.detailCloseDist || v.y <= -b.detailFlick;
    // 판정도 잠긴 축을 따른다 — 드래그 중 보이던 것과 손 뗀 결과가 달라지면 안 된다.
    const horizontal = d.axisLock === 1;
    const still = Math.abs(d.dragX) < b.dragDeadzone && Math.abs(d.dragY) < b.dragDeadzone;

    if (still) {
      this.beginCloseDetail();
      return;
    }
    if (horizontal && navOk) {
      const dir = d.dragX < 0 ? 1 : -1;
      const nextSlot = this.nextOwnedSlot(d.slot, dir);
      const atEdge = nextSlot === 0;
      if (atEdge) {
        d.phase = 'shown';
        d.bActive = false;
        this.onEvent?.('detail-edge');
        return;
      }
      d.navDir = dir;
      if (!d.bActive || d.bSlot !== nextSlot) this.prepareBSlot(nextSlot);
      d.bx = d.ax + dir * this.screenW;
      d.phase = 'nav';
      d.avx = -v.x;
      // 세로는 죽인다. 가로로 넘기는 중에 dragY 잔여 속도가 남으면 카드가 위아래로
      // 출렁이며 넘어간다 (2026-09-19 사용자 보고). 넘기기는 가로 운동이다.
      d.avy = 0;
      d.ay = b.detailY;
      d.by = b.detailY;
      d.bvy = 0;
      return;
    }
    if (!horizontal && closeOk) {
      this.beginCloseDetail();
      return;
    }
    // 미달 — 중앙으로 복귀
    d.phase = 'shown';
    d.bActive = false;
    d.avx = 0;
    d.avy = 0;
  }

  private beginCloseDetail(): void {
    const d = this.det;
    // 넘기는 동안 다른 면의 카드로 갔을 수 있다. 면 이동은 **닫는 이 순간에** 한다
    // (nav 중에 바꾸면 viewX 가 튀어 카드가 되돌아온다 — 2026-09-19).
    const nf = slotToFace(d.slot);
    if (nf !== this.face) {
      this.face = nf;
      this.resetSeg();
      // 카드는 화면 중앙에 떠 있다. 면이 바뀌면 viewX 가 달라지므로 현재 위치를
      // 새 좌표계로 옮겨 놔야 닫힘이 제자리에서 시작한다.
      d.ax = this.viewX;
    }
    d.phase = 'closing';
    d.stage = 2;
    d.bActive = false;
    this.onEvent?.('detail-close');
  }

  /** 연출 없이 즉시 닫는다 (B / ✕). */
  private snapCloseDetail(): void {
    const d = this.det;
    // 즉시 닫을 때도 면을 맞춰 둬야 다음에 그 카드가 제 칸에 있다.
    const nf = slotToFace(d.slot);
    if (nf !== this.face) {
      this.face = nf;
      this.resetSeg();
    }
    d.phase = 'opening';
    d.stage = 1;
    d.az = BINDER_LAYERS.card;
    d.avz = 0;
    d.bActive = false;
    d.dim = 0;
    d.dimV = 0;
    d.dragging = false;
    this.mode = 'idle';
    audio.setFriction(0);
    this.onEvent?.('detail-closed');
  }

  // ---------------------------------------------------------------- RS
  writeRS(): void {
    const r = this.rs;
    const s = this.seg;
    r.u = s.u;
    r.riseY = this.stow.active ? this.stow.riseY : 0;
    r.face = this.face;
    r.mode = MODE_ID[this.mode];
    r.segKind = s.kind;
    r.segDir = s.dir;
    r.segFrom = s.from;
    r.segTo = s.to;
    r.segSheet = s.sheet;
    const st = this.stow;
    for (let i = 0; i < 8; i++) {
      const on = st.active && st.live[i] && st.started[i] && !st.landed[i] ? 1 : 0;
      r.svis[i] = on;
      r.sx[i] = st.x[i];
      r.sy[i] = st.y[i];
      r.ss[i] = st.s[i];
      r.sslot[i] = st.live[i] ? st.slot[i] : 0;
      r.slanded[i] = st.live[i] && st.landed[i] ? 1 : 0;
    }
    const d = this.det;
    const active = this.mode === 'detail';
    r.detActive = active ? 1 : 0;
    r.detPhase = PHASE_ID[d.phase];
    r.detCell = d.cell;
    r.detSlotA = active ? d.slot : 0;
    r.detSlotB = active && d.bActive ? d.bSlot : 0;
    r.detOwnedA = d.owned ? 1 : 0;
    r.detOwnedB = d.bOwned ? 1 : 0;
    r.detBVis = active && d.bActive ? 1 : 0;
    r.ax = d.ax;
    r.ay = d.ay;
    r.as = d.as;
    r.az = d.az;
    r.bx = d.bx;
    r.by = d.by;
    r.bs = d.bs;
    r.bz = d.bz;
    r.dim = d.dim;
  }
}
